/**
 * useMessages.js — direct messaging.
 *
 * WHY CONVERSATIONS WERE NOT SAVING
 *
 * Three independent bugs in the original, any one of which loses a thread:
 *
 *  1. Random conversation IDs. Both people opening a thread at the same moment
 *     created two conversations, and each then saw an empty one. Fixed by
 *     deriving the ID from the two uids, sorted — the same pair can only ever
 *     produce one document.
 *
 *  2. Deleting was `setBlockedConvs([...prev, id])` in component state. It
 *     survived exactly until the component unmounted. Now `archivedBy` is a
 *     real field, so hiding a thread persists and can be undone.
 *
 *  3. The conversation document was only written when the FIRST message was
 *     sent, but the thread was opened before that. Navigate away before typing
 *     and the thread was gone. Now the conversation document is created when
 *     the thread opens, and shown in the list immediately.
 *
 * Unread counts are written by a Cloud Function trigger, never the client — a
 * client-owned counter can be zeroed on someone else's behalf and drifts
 * permanently whenever a tab closes mid-write.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, doc, query, where, orderBy, limit, startAfter,
  onSnapshot, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  serverTimestamp, arrayUnion, arrayRemove, writeBatch, deleteField,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { cleanText } from '../lib/safe';
import { fullName } from '../lib/format';

const MESSAGE_PAGE = 40;

/**
 * Fail early, and say why.
 *
 * `canWrite()` in firestore.rules requires a verified email, so an unconfirmed
 * account gets "Missing or insufficient permissions" from Firestore — a string
 * that describes the mechanism and not the cause, and that no user can act on.
 * Checking here turns it into an instruction, and avoids a pointless write.
 */
function assertCanWrite() {
  if (auth.currentUser && auth.currentUser.emailVerified === false) {
    throw new Error(
      'Confirm your email address before messaging — check your inbox for the link.'
    );
  }
}

/** Anything else Firestore refuses, in words. */
export function messageError(err) {
  const code = err?.code ?? '';
  const msg = err?.message ?? '';

  if (code === 'permission-denied' || msg.includes('insufficient permissions')) {
    if (auth.currentUser?.emailVerified === false) {
      return 'Confirm your email address before messaging — check your inbox for the link.';
    }
    return 'You cannot message this person. They may have blocked you, or your account may be restricted.';
  }
  if (code === 'unavailable') return 'You appear to be offline. This will send when you reconnect.';
  return msg || 'Something went wrong. Please try again.';
}

/** Deterministic, and identical to conversationId() in functions/lib/guards.js. */
export function conversationId(a, b) {
  return a < b ? `${a}__${b}` : `${b}__${a}`;
}

const normalise = (snap) => ({ id: snap.id, ...snap.data() });

/** Metadata denormalised into the conversation so the list renders without N reads. */
function participantMeta(profile) {
  return {
    displayName: fullName(profile),
    username: profile.usernameLower ?? null,
    avatarUrl: profile.avatarUrl ?? null,
    avatarColour: profile.avatarColour ?? '#1F5D52',
    jobTitle: profile.jobTitle ?? '',
  };
}

/* ── Conversation list ─────────────────────────────────────────────────────── */

export function useConversations(uid) {
  const [conversations, setConversations] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!uid) {
      setConversations([]);
      setStatus('empty');
      return undefined;
    }
    setStatus('loading');

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc'),
      limit(60)
    );

    return onSnapshot(
      q,
      (snap) => {
        setConversations(snap.docs.map(normalise));
        setStatus(snap.empty ? 'empty' : 'ready');
      },
      (err) => {
        console.error('Conversations subscription failed', err);
        setStatus('error');
      }
    );
  }, [uid]);

  /**
   * Split into the inbox and a request folder.
   *
   * Anyone can message anyone on a professional network, which is correct — but
   * an unfiltered inbox is how harassment arrives. A thread started by someone
   * you do not follow lands in Requests until you reply, and never rings a
   * notification.
   */
  const { inbox, requests, archived, totalUnread } = useMemo(() => {
    const inboxList = [];
    const requestList = [];
    const archivedList = [];
    let unread = 0;

    for (const c of conversations) {
      if ((c.archivedBy ?? []).includes(uid)) { archivedList.push(c); continue; }

      const isRequest = c.requestPending === true && c.requestedBy !== uid;
      (isRequest ? requestList : inboxList).push(c);

      // Muted threads still show their own badge in the list — muting stops the
      // interruption, it does not pretend the message never arrived. What it
      // does suppress is the global count in the tab bar.
      if (!isRequest && !(c.mutedBy ?? []).includes(uid)) {
        unread += c.unread?.[uid] ?? 0;
      }
    }

    // Pinned threads sit at the top, still newest-first among themselves.
    inboxList.sort((a, b) => {
      const ap = (a.pinnedBy ?? []).includes(uid) ? 1 : 0;
      const bp = (b.pinnedBy ?? []).includes(uid) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      return (b.lastMessageAt?.toMillis?.() ?? 0) - (a.lastMessageAt?.toMillis?.() ?? 0);
    });

    return {
      inbox: inboxList,
      requests: requestList,
      archived: archivedList,
      totalUnread: unread,
    };
  }, [conversations, uid]);

  return { conversations, inbox, requests, archived, totalUnread, status };
}

/**
 * One conversation, subscribed directly.
 *
 * The list query stops at 60 threads, so a link to an older conversation — from
 * a notification, or a bookmark — would otherwise land on "select a
 * conversation" with the thread nowhere in sight. `enabled` lets the caller skip
 * the read entirely when the thread is already in the list it has.
 */
export function useConversation(convId, enabled = true) {
  const [conversation, setConversation] = useState(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!convId || !enabled) {
      setConversation(null);
      setStatus('idle');
      return undefined;
    }
    setStatus('loading');

    return onSnapshot(
      doc(db, 'conversations', convId),
      (snap) => {
        setConversation(snap.exists() ? normalise(snap) : null);
        setStatus(snap.exists() ? 'ready' : 'missing');
      },
      () => { setConversation(null); setStatus('error'); }
    );
  }, [convId, enabled]);

  return { conversation, status };
}

/* ── Messages in one thread ────────────────────────────────────────────────── */

export function useConversationMessages(convId, uid) {
  const [messages, setMessages] = useState([]);
  const [older, setOlder] = useState([]);
  const [status, setStatus] = useState('idle');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const oldestDoc = useRef(null);

  useEffect(() => {
    setOlder([]);
    oldestDoc.current = null;

    if (!convId) {
      setMessages([]);
      setStatus('idle');
      return undefined;
    }
    setStatus('loading');

    // Newest-first from Firestore, reversed for display. Fetching descending is
    // what lets us paginate backwards through history.
    const q = query(
      collection(db, 'conversations', convId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(MESSAGE_PAGE)
    );

    return onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map(normalise).reverse();
        setMessages(docs);
        oldestDoc.current = snap.docs[snap.docs.length - 1] ?? null;
        setHasMore(snap.docs.length === MESSAGE_PAGE);
        setStatus(docs.length ? 'ready' : 'empty');
      },
      (err) => {
        console.error('Messages subscription failed', err);
        setStatus('error');
      }
    );
  }, [convId]);

  const loadOlder = useCallback(async () => {
    if (!convId || !hasMore || loadingMore || !oldestDoc.current) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'conversations', convId, 'messages'),
        orderBy('createdAt', 'desc'),
        startAfter(oldestDoc.current),
        limit(MESSAGE_PAGE)
      );
      const snap = await getDocs(q);
      setOlder((prev) => [...snap.docs.map(normalise).reverse(), ...prev]);
      oldestDoc.current = snap.docs[snap.docs.length - 1] ?? oldestDoc.current;
      setHasMore(snap.docs.length === MESSAGE_PAGE);
    } catch (err) {
      console.error('loadOlder failed', err);
    } finally {
      setLoadingMore(false);
    }
  }, [convId, hasMore, loadingMore]);

  const all = useMemo(() => {
    const seen = new Set();
    return [...older, ...messages].filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [older, messages]);

  return { messages: all, status, hasMore, loadingMore, loadOlder };
}

/* ── Read receipts and typing ──────────────────────────────────────────────── */

/**
 * One switch for read receipts and typing indicators together.
 *
 * They are the same promise — "I will tell you what I am doing with your
 * message" — and every product that splits them ends up with people who think
 * they turned off one and quietly kept broadcasting the other.
 *
 * Turning it off is reciprocal: you stop sending receipts, and you stop
 * receiving them. A setting that lets you watch other people while staying
 * invisible yourself is a surveillance feature, not a privacy one.
 */
export function useReadReceipts(uid) {
  // Default on. A messaging product with receipts silently off for everyone is
  // just a broken messaging product.
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(
      doc(db, 'users', uid, 'private', 'settings'),
      (snap) => setEnabled(snap.data()?.readReceipts !== false),
      () => setEnabled(true)
    );
  }, [uid]);

  return enabled;
}

/* ── Typing indicator ──────────────────────────────────────────────────────── */

export function useTypingIndicator(convId, uid, otherUid) {
  const [otherTyping, setOtherTyping] = useState(false);
  const lastPing = useRef(0);

  useEffect(() => {
    if (!convId || !otherUid) return undefined;
    return onSnapshot(
      doc(db, 'conversations', convId, 'typing', otherUid),
      (snap) => {
        const at = snap.data()?.at?.toMillis?.() ?? 0;
        // Treat a stale ping as "stopped": a closed tab never sends the clear.
        setOtherTyping(Date.now() - at < 6000);
      },
      () => setOtherTyping(false)
    );
  }, [convId, otherUid]);

  // Expire the indicator locally too, so it does not stick when the other
  // person stops typing without sending.
  useEffect(() => {
    if (!otherTyping) return undefined;
    const t = setTimeout(() => setOtherTyping(false), 6000);
    return () => clearTimeout(t);
  }, [otherTyping]);

  const ping = useCallback(() => {
    if (!convId || !uid) return;
    // At most one write every 3 seconds. Without throttling this is one
    // Firestore write per keystroke, which is a genuine cost problem.
    const now = Date.now();
    if (now - lastPing.current < 3000) return;
    lastPing.current = now;
    setDoc(doc(db, 'conversations', convId, 'typing', uid), {
      at: serverTimestamp(),
    }).catch(() => {});
  }, [convId, uid]);

  const clear = useCallback(() => {
    if (!convId || !uid) return;
    lastPing.current = 0;
    deleteDoc(doc(db, 'conversations', convId, 'typing', uid)).catch(() => {});
  }, [convId, uid]);

  return { otherTyping, ping, clear };
}

/* ── Actions ───────────────────────────────────────────────────────────────── */

export function useMessageActions({ profile, onError }) {
  const uid = profile?.uid;

  /**
   * Open (or create) the thread with someone. Idempotent by construction.
   *
   * @returns {Promise<string>} conversation ID
   */
  const openConversation = useCallback(async (other) => {
    if (!uid) throw new Error('Sign in to send messages.');
    assertCanWrite();

    // Accepts a profile object or a bare uid. The "Message" item on a post only
    // knows the author's id, and making every caller fetch a full profile first
    // is how that link ended up doing nothing.
    let otherProfile = typeof other === 'string' ? null : other;
    const otherUid = typeof other === 'string' ? other : other?.uid;

    if (!otherUid) throw new Error('Cannot open that conversation.');
    if (otherUid === uid) throw new Error('You cannot message yourself.');

    if (!otherProfile) {
      const snap = await getDoc(doc(db, 'users', otherUid));
      if (!snap.exists()) throw new Error('That account no longer exists.');
      otherProfile = { uid: otherUid, ...snap.data() };
    }
    otherProfile = { ...otherProfile, uid: otherUid };

    const convId = conversationId(uid, otherProfile.uid);
    const ref = doc(db, 'conversations', convId);
    const existing = await getDoc(ref);

    if (existing.exists()) {
      // Refresh the cached names/avatars — someone may have changed theirs, and
      // a stale avatar in a message list looks like a bug.
      await updateDoc(ref, {
        [`participantMeta.${uid}`]: participantMeta(profile),
        archivedBy: arrayRemove(uid),
      }).catch(() => {});
      return convId;
    }

    // Do I already follow them? If not, this starts life as a request.
    const followSnap = await getDoc(
      doc(db, 'users', otherProfile.uid, 'followers', uid)
    ).catch(() => null);
    const theyFollowMe = followSnap?.exists() ?? false;

    await setDoc(ref, {
      participants: [uid, otherProfile.uid].sort(),
      participantMeta: {
        [uid]: participantMeta(profile),
        [otherProfile.uid]: participantMeta(otherProfile),
      },
      createdAt: serverTimestamp(),
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      lastSenderId: null,
      unread: { [uid]: 0, [otherProfile.uid]: 0 },
      requestPending: !theyFollowMe,
      requestedBy: uid,
      archivedBy: [],
      mutedBy: [],
      pinnedBy: [],
    });

    return convId;
  }, [uid, profile]);

  const sendMessage = useCallback(async (
    convId,
    { text, media = [], replyTo = null }
  ) => {
    if (!uid) throw new Error('Sign in to send messages.');
    assertCanWrite();
    const body = cleanText(text, 4000);
    if (!body && media.length === 0) return null;

    const ref = doc(collection(db, 'conversations', convId, 'messages'));

    const payload = {
      id: ref.id,
      senderId: uid,
      text: body,
      media: media.slice(0, 3),
      replyToId: replyTo?.id ?? null,
      createdAt: serverTimestamp(),
      editedAt: null,
      deleted: false,
      readBy: [uid],
      system: false,
    };

    // Deliberately no copy of the quoted text. Storing one would let a client
    // write whatever it liked into a quote attributed to the other person, and
    // Firestore rules cannot check a 200-character truncation against a
    // 4000-character original. The reply renders from the real message.

    try {
      await setDoc(ref, payload);
      return ref.id;
    } catch (err) {
      console.error('sendMessage failed', err);
      onError?.(messageError(err));
      throw err;
    }
  }, [uid, onError]);

  /**
   * One reaction per person per message, stored under their uid.
   *
   * Passing the same emoji twice removes it, which is what every chat client
   * does and what a second tap on the same button should mean.
   */
  const reactToMessage = useCallback(async (convId, messageId, emoji) => {
    if (!uid) return;
    const ref = doc(db, 'conversations', convId, 'messages', messageId);
    try {
      await updateDoc(ref, {
        [`reactions.${uid}`]: emoji ? String(emoji).slice(0, 20) : deleteField(),
      });
    } catch (err) {
      console.error('reactToMessage failed', err);
      onError?.('Could not add that reaction.');
    }
  }, [uid, onError]);

  /**
   * Clear my unread badge and, if receipts are on, mark visible messages read.
   *
   * The badge is mine and always clears. The receipt is a disclosure to the
   * other person and is the part the setting governs — conflating the two is
   * how "turn off read receipts" ends up also breaking your own unread count.
   */
  const markRead = useCallback(async (convId, messageIds = [], receipts = true) => {
    if (!uid || !convId) return;

    updateDoc(doc(db, 'conversations', convId), {
      [`unread.${uid}`]: 0,
    }).catch(() => {});

    if (!receipts) return;

    // Read receipts, batched. Capped so opening a long backlog is not 500 writes.
    const unmarked = messageIds.slice(-30);
    if (!unmarked.length) return;

    const batch = writeBatch(db);
    for (const id of unmarked) {
      batch.update(doc(db, 'conversations', convId, 'messages', id), {
        readBy: arrayUnion(uid),
      });
    }
    batch.commit().catch(() => {});
  }, [uid]);

  /** Accepting a request moves it into the main inbox for both people. */
  const acceptRequest = useCallback(async (convId) => {
    await updateDoc(doc(db, 'conversations', convId), { requestPending: false });
  }, []);

  /** Hide a thread for me only. The other person keeps their copy — as on every
   *  major platform, you cannot delete a conversation out of someone else's
   *  account. The UI says so explicitly. */
  const archiveConversation = useCallback(async (convId, archived = true) => {
    await updateDoc(doc(db, 'conversations', convId), {
      archivedBy: archived ? arrayUnion(uid) : arrayRemove(uid),
    });
  }, [uid]);

  /** Unsend: tombstone, not erase, so the thread does not silently reshuffle. */
  const unsendMessage = useCallback(async (convId, messageId) => {
    await updateDoc(doc(db, 'conversations', convId, 'messages', messageId), {
      deleted: true,
      text: '',
    });
  }, []);

  /** Silence notifications for one thread without leaving it. */
  const muteConversation = useCallback(async (convId, muted = true) => {
    await updateDoc(doc(db, 'conversations', convId), {
      mutedBy: muted ? arrayUnion(uid) : arrayRemove(uid),
    });
  }, [uid]);

  /** Keep a thread at the top of my own list. Invisible to the other person. */
  const pinConversation = useCallback(async (convId, pinned = true) => {
    await updateDoc(doc(db, 'conversations', convId), {
      pinnedBy: pinned ? arrayUnion(uid) : arrayRemove(uid),
    });
  }, [uid]);

  return {
    openConversation,
    sendMessage,
    reactToMessage,
    markRead,
    acceptRequest,
    archiveConversation,
    muteConversation,
    pinConversation,
    unsendMessage,
  };
}

/* ── Attachments ───────────────────────────────────────────────────────────── */

export const DM_MAX_ATTACHMENTS = 3;
export const DM_MAX_BYTES = 10 * 1024 * 1024;
export const DM_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/avif,application/pdf';

/**
 * Only render or link media we can prove came from our own bucket.
 *
 * `media` is a list, and Firestore rules cannot inspect a list's contents — so
 * a hand-crafted write could put any URL in there and the other participant's
 * browser would fetch it, leaking their IP and read-time to a third party. The
 * Content-Security-Policy already blocks the image case; this closes the link
 * case too, and means one bad entry does not poison the whole message.
 */
export function isSafeMediaUrl(url) {
  if (typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    // Exact host only, matching the Content-Security-Policy in firebase.json.
    // A suffix match on .firebasestorage.app would accept any bucket anyone
    // happens to own on that domain.
    return u.protocol === 'https:'
      && u.hostname === 'firebasestorage.googleapis.com';
  } catch {
    return false;
  }
}
